import { createRouter, createWebHistory } from 'vue-router';

import { Role } from './roles/entities/Role';
import { verifyTokenMiddleware } from '@/auth/middlewares/verifyTokenMiddleware.js';
import { storeClassificationsMiddleware } from './classifications/middlewares/storeClassificationsMiddleware';
import { verifyRoleMiddleware } from './roles/middlewares/verifyRoleMiddleware';
import { verifyPoliciesMiddleware } from './auth/middlewares/verifyPolicyMiddleware';
import { verifyTermsMiddleware } from './auth/middlewares/verifyTermsMiddleware';

// TODO: 🔴 Proteger las rutas para que los profesores no puedan ver las páginas de otros profesores
const router = createRouter({
    history: createWebHistory(),
    routes: [

        // RUTAS QUE REQUIEREN AUTENTICACIÓN
        {
            path: '/',
            component: () => import('@/auth/layouts/AppLayout.vue'),
            meta: {
                requiresAuth: true
            },

            // TODO: 🟢 agrupar los modulos por archivo
            // TODO: Considerar añadir el concepto de "Section"
            children: [

                // 🚀 INICIO
                {
                    path: '/',
                    component: () => import('@/auth/pages/HomeModule.vue'),
                    meta: {
                        title: 'Inicio'
                    },
                    children: [
                        {
                            path: '/inicio',
                            name: 'home',
                            component: () => import('@/auth/pages/HomePage.vue'),
                        }
                    ]
                },

                // 🚀 TÉRMINOS Y CONDICIONES
                {
                    path: '/terminos-y-condiciones',
                    name: 'terms-and-conditions',
                    component: () => import('@/auth/pages/TermsAndConditionsPage.vue'),
                    meta: {
                        title: 'Términos y Condiciones'
                    }
                },

                // 🚀 PERFIL ACADÉMICO - DATOS PERSONALES
                {
                    path: '/',
                    component: () => import('@/professors/modules/PersonalDataModule.vue'),
                    meta: {
                        title: 'Perfil académico > Datos personales',
                    },
                    children: [
                        {
                            path: 'personas/:personId/informacion-personal',
                            name: 'persons.personal-information',
                            component: () => import('@/professors/pages/PersonalInformationPage.vue'),
                            meta: {
                                description: 'Información personal del académico. Los datos mostrados son informativos y provienen del sistema CAPA.'
                            }
                        }, {
                            path: 'personas/:personId/direcciones',
                            name: 'persons.addresses',
                            component: () => import('@/professors/pages/AddressesPage.vue'),
                            meta: {
                                description: 'Datos de contacto del académico.'
                            }
                        }
                    ]
                },

                // 🚀 PERFIL ACADÉMICO - RESUMEN PROFESIONAL
                {
                    path: '/',
                    component: () => import('@/professors/modules/ResumeModule.vue'),
                    meta: {
                        title: 'Perfil académico > Resumen profesional'
                    },
                    children: [
                        {
                            path: 'personas/:personId/resumen-profesional',
                            name: 'persons.resume',
                            component: () => import('@/professors/pages/ResumePage.vue'),
                            meta: {
                                description: 'Resumen profesional del académico, detalles de su experiencia académica, trayectoria, áreas de especialización y principales logros.'
                            }
                        }
                    ]
                },

                // 🚀 PERFIL ACADÉMICO - DATOS CONTRATUALES
                {
                    path: '/',
                    component: () => import('@/professors/modules/ContractualDataModule.vue'),
                    meta: {
                        title: 'Perfil académico > Datos contratuales'
                    },
                    children: [{
                        path: 'personas/:personId/ingreso-y-ubicacion',
                        name: 'persons.entrance-and-location',
                        component: () => import('@/professors/pages/EntranceAndLocationPage.vue'),
                        meta: {
                            description: 'Ubicación dentro de la Facultad y fechas de ingreso del académico. Los datos son informativos y provienen del sistema CAPA.'
                        }
                    }, {
                        path: 'personas/:personId/nombramientos',
                        name: 'persons.appointments',
                        component: () => import('@/professors/pages/AppointmentsPage.vue'),
                        meta: {
                            description: 'Un nombramiento académico es el cargo oficial que una persona ocupa en una facultad o entidad universitaria, indicando su puesto, horas por semana y periodo de vigencia.'
                        }
                    }, {
                        path: 'personas/:personId/cargos',
                        name: 'persons.administrative-positions',
                        component: () => import('@/professors/pages/AdministrativePositionsPage.vue'),
                        meta: {
                            description: 'Los cargos administrativos son puestos oficiales que el académico ocupa dentro de la institución para tareas de gestión o apoyo, indicando horas asignadas y periodo de vigencia.'
                        }
                    }, {
                        path: 'personas/:personId/empleos',
                        name: 'persons.employments',
                        component: () => import('@/professors/pages/EmploymentsPage.vue'),
                        meta: {
                            description: 'Los empleos son puestos o cargos que el académico ha tenido en empresas u organizaciones externas, registrando la compañía, posición y periodo de trabajo. Permiten llevar un historial laboral fuera del ámbito académico o administrativo institucional.'
                        }
                    }
                    ]
                },

                // 🚀 PERFIL ACADÉMICO - FORMACIÓN Y SUPERACIÓN ACADEMICA
                {
                    path: '/',
                    component: () => import('@/professors/modules/EducationModule.vue'),
                    meta: {
                        title: 'Perfil académico > Formación y superación académica'
                    },

                    children: [
                        {
                            path: 'personas/:personId/educacion',
                            name: 'persons.education',
                            component: () => import('@/professors/pages/EducationPage.vue'),
                            meta: {
                                description: "La formación académica corresponde a los estudios realizados por el académico en los distintos niveles educativos, desde el bachillerato hasta el posdoctorado."
                            }
                        },
                        {
                            path: 'personas/:personId/actividades',
                            name: 'persons.activities',
                            component: () => import('@/professors/pages/ActivitiesPage.vue'),
                            meta: {
                                description: "Las actividades de superación académica son cursos, estancias, seminarios, talleres y otras actividades formativas realizadas por el académico para fortalecer y actualizar su desarrollo profesional."
                            }
                        },
                        {
                            path: 'personas/:personId/idiomas',
                            name: 'persons.languages',
                            component: () => import('@/professors/pages/LanguagesPage.vue'),
                            meta: {
                                description: "Los idiomas corresponden a las lenguas que domina el académico y al nivel de competencia que posee para leer, hablar y escribir cada una."
                            }
                        },
                    ]
                },

                // 🚀 PERFIL ACADÉMICO - PREMIOS Y DISTINCIONES
                {
                    path: '/',
                    component: () => import('@/professors/modules/AwardModule.vue'),
                    meta: {
                        title: 'Perfil académico > Premios y distinciones'
                    },

                    children: [
                        {
                            path: 'personas/:personId/premios',
                            name: 'persons.awards',
                            component: () => import('@/professors/pages/AwardsPage.vue'),
                            meta: {
                                description: "Los premios y distinciones son reconocimientos otorgados al académico por su desempeño, méritos, trayectoria o aportaciones en el ámbito académico, científico o profesional."
                            }
                        }
                    ]
                },

                // 🚀 PERFIL ACADÉMICO - PROGRAMAS DE APOYO A LA DOCENCIA E INVESTIGACIÓN
                {
                    path: '/',
                    component: () => import('@/professors/modules/FinancingModule.vue'),
                    meta: {
                        title: 'Perfil académico > Programas de apoyo a la Docencia e Investigación'
                    },

                    children: [
                        {
                            path: 'personas/:personId/proyectos/unam',
                            name: 'persons.projects.unam',
                            component: () => import('@/professors/pages/UnamProjectsPage.vue'),
                            meta: {
                                description: "Los programas de apoyo a la docencia e investigación son convocatorias y proyectos en los que el académico participa para fortalecer su labor académica."
                            }
                        },
                    ]
                },

                // 🚀 PERFIL ACADÉMICO - ASOCIACIONES ACADEMICAS Y PROFESIONALES
                {
                    path: '/',
                    component: () => import('@/professors/modules/AssociationModule.vue'),
                    meta: {
                        title: 'Perfil académico > Asociaciones académicas y profesionales'
                    },

                    children: [
                        {
                            path: 'personas/:personId/asociaciones',
                            name: 'persons.associations',
                            component: () => import('@/professors/pages/AssociationsPage.vue'),
                            meta: {
                                description: "Las asociaciones corresponden a las organizaciones académicas, científicas o profesionales a las que pertenece el académico, indicando la función desempeñada y el periodo de participación."
                            }
                        },
                    ]
                },

                // 🚀 APOYO INSTITUCIONAL - APOYO ACADEMICO
                {
                    path: '/',
                    component: () => import('@/professors/modules/SupportModule.vue'),
                    meta: {
                        title: 'Apoyo Institucional > Apoyo académico'
                    },
                    children: [{
                        path: 'personas/:personId/apoyos',
                        name: 'persons.supports',
                        component: () => import('@/professors/pages/SupportPage.vue'),
                        meta: {
                            description: "Los apoyos académicos corresponden a actividades adicionales del académico, como la coordinación de asignaturas, cursos intersemestrales, jefaturas de laboratorio o participación en convenios."
                        }
                    }]
                },

                // 🚀 APOYO INSTITUCIONAL - COMISIONES
                {
                    path: '/',
                    component: () => import('@/professors/modules/ComissionsModule.vue'),
                    meta: {
                        title: 'Apoyo Institucional > Comisiones'
                    },
                    children: [{
                        path: 'personas/:personId/comisiones',
                        name: 'persons.commissions.list',
                        component: () => import('@/professors/pages/ComissionsListPage.vue'),
                        meta: {
                            description: "Encargos académicos para evaluar, dictaminar o atender funciones institucionales (p. ej., PRIDE, jurados de concurso, comisiones dictaminadoras o comisiones especiales)."
                        }
                    }]
                },

                // 🚀 APOYO INSTITUCIONAL - ORGANOS COLEGIADOS/ASESORES
                {
                    path: '/',
                    component: () => import('@/professors/modules/CollegiateCommittesModule.vue'),
                    meta: {
                        title: 'Apoyo Institucional > Órganos asesores/colegiados'
                    },
                    children: [
                        {
                            path: 'personas/:personId/organos',
                            name: 'persons.committes.list',
                            component: () => import('@/professors/pages/CommittesListPage.vue'),
                            meta: {
                                description: "Los órganos asesores y órganos colegiados son instancias institucionales en las que el académico participa para apoyar, evaluar o tomar decisiones en materia académica, administrativa o de gobierno universitario."
                            }
                        }
                    ]
                },

                // 🚀 APOYO INSTITUCIONAL - PROGRAMAS DE TUTORIAS
                {
                    path: '/',
                    component: () => import('@/professors/modules/TutoringsModule.vue'),
                    meta: {
                        title: 'Apoyo Institucional > Programas de tutorías'
                    },
                    children: [{
                        path: 'personas/:personId/tutorias',
                        name: 'persons.tutorings',
                        component: () => import('@/professors/pages/TutoringsPage.vue'),
                        meta: {
                            description: "Registro de la participación del académico como tutor en programas institucionales de tutoría, indicando al estudiante atendido, la carrera y el periodo de acompañamiento."
                        }
                    }]
                },

                // 🚀 DOCENCIA - CURSOS
                {
                    path: '/',
                    component: () => import('@/professors/modules/CoursesModule.vue'),
                    meta: {
                        title: 'Docencia > Cursos'
                    },
                    children: [{
                        path: 'personas/:personId/cursos/quimica',
                        name: 'persons.courses',
                        component: () => import('@/professors/pages/FacultyCoursesPage.vue'),
                        meta: {
                            description: "Relación de las asignaturas de licenciatura impartidas por el académico en la Facultad, con información de semestre, grupo, tipo de asignatura, horas y número de alumnos."
                        }
                    }, {
                        path: 'personas/:personId/cursos/posgrado',
                        name: 'persons.courses.postgraduate',
                        component: () => import('@/professors/pages/PostgraduateCoursesPage.vue'),
                        meta: {
                            description: "Registro de las asignaturas impartidas por el académico en programas de posgrado, indicando nivel, programa, tipo de enseñanza, semestre y horas asignadas."
                        }
                    }, {
                        path: 'personas/:personId/cursos/extracurriculares',
                        name: 'persons.courses.extracurriculars',
                        component: () => import('@/professors/pages/ExtracurricularCoursesPage.vue'),
                        meta: {
                            description: "Registro de los cursos extracurriculares impartidos por el académico, como cursos propedéuticos, de formación docente o de educación continua, indicando duración y número de alumnos."
                        }
                    }, {
                        path: 'personas/:personId/cursos/externos',
                        name: 'persons.courses.external',
                        component: () => import('@/professors/pages/ExternalCoursesPage.vue'),
                        meta: {
                            description: "Incluye cursos tomados por el académico fuera de la Facultad de Química, por ejemplo en otras universidades, centros educativos o instituciones académicas, en distintos niveles educativos."
                        }
                    }]
                },

                // 🚀 DOCENCIA - FORMACIÓN DE RECURSOS HUMANOS
                {
                    path: '/',
                    component: () => import('@/professors/modules/TitulationsModule.vue'),
                    meta: {
                        title: 'Docencia > Formación de recursos humanos'
                    },
                    children: [{
                        path: 'personas/:personId/servicios-sociales',
                        name: 'persons.social-services',
                        component: () => import('@/professors/pages/SocialServicesPage.vue'),
                        meta: {
                            description: "Registro de estudiantes que realizan su servicio social bajo la supervisión del académico, indicando institución, carrera y periodo de realización."
                        }
                    }, {
                        path: 'personas/:personId/tesis',
                        name: 'persons.thesis',
                        component: () => import('@/professors/pages/ThesisPage.vue'),
                        meta: { 
                            description: "Registro de tesis y trabajos de titulación en los que participa el académico, ya sea como director, asesor, sinodal o miembro del jurado, en licenciatura y posgrado."
                        }
                    }, {
                        path: 'personas/:personId/jurados',
                        name: 'persons.juries',
                        component: () => import('@/professors/pages/JuriesPage.vue'),
                        meta: {
                            description: "Registro de la participación del académico como jurado o sinodal en exámenes de titulación y de grado, en licenciatura y posgrado."
                        }
                    }, {
                        path: 'personas/:personId/tutorias/posgrado',
                        name: 'persons.tutorings.postgraduate',
                        component: () => import('@/professors/pages/PostgraduateTutoringsPage.vue'),
                        meta: {
                            description: "Registro de la participación del académico como tutor de estudiantes de posgrado, indicando programa y periodo de tutoría."
                        }
                    }]
                },

                // 🚀 DOCENCIA - DISEÑO DE MATERIAL DIDÁCTICO
                {
                    path: '/',
                    component: () => import('@/professors/modules/MaterialsModule.vue'),
                    meta: {
                        title: 'Docencia > Diseño de material didáctico'
                    },
                    children: [{
                        path: 'personas/:personId/materiales',
                        name: 'persons.materials',
                        component: () => import('@/professors/pages/MaterialsPage.vue'),
                        meta: {
                            description: "Registro del material didáctico desarrollado por el académico para apoyar la enseñanza en distintos niveles educativos."
                        }
                    }]
                },

                // 🚀 DOCENCIA - ASESORÍAS
                {
                    path: '/',
                    component: () => import('@/professors/modules/StudentGuidancesModule.vue'),
                    meta: {
                        title: 'Docencia > Asesorías',
                    },
                    children: [
                        {
                            path: 'personas/:personId/asesorias/profesores',
                            name: 'persons.guidance.professors',
                            component: () => import('@/professors/pages/ProfessorGuidancesPage.vue'),
                            meta: {
                                description: "Registro de las asesorías académicas brindadas a otros profesores para apoyar actividades de docencia, actualización o mejora de programas y asignaturas."
                            }
                        },
                        {
                            path: 'personas/:personId/asesorias/alumnos',
                            name: 'persons.guidance.students',
                            component: () => import('@/professors/pages/StudentGuidancesPage.vue'),
                            meta: {
                                description: "Incluye actividades como asesorías académicas a nivel licenciatura, apoyo en asignaturas específicas y visitas industriales con grupos de alumnos, indicando número de estudiantes y periodo de atención."
                            }
                        },
                    ],
                },

                // 🚀 DOCENCIA - ARBITRAJES DE DOCENCIA
                {
                    path: '/',
                    component: () => import('@/professors/modules/AcademicArbitrationsModule.vue'),
                    meta: {
                        title: 'Docencia > Arbitrajes de Docencia'
                    },
                    children: [{
                        path: 'personas/:personId/arbitrajes/docencia',
                        name: 'persons.academic-arbitrations',
                        component: () => import('@/professors/pages/AcademicArbitrationsPage.vue'),
                        meta: {
                            description: "Registro de la participación del académico como árbitro o evaluador de trabajos académicos en el ámbito de la docencia."
                        }
                    }]
                },

                // 🚀 INVESTIGACIÓN - PRODUCTOS DE LA INVESTIGACIÓN
                {
                    path: '/',
                    component: () => import('@/professors/modules/ResearchProductsModule.vue'),
                    meta: {
                        title: 'Investigación > Productos de la investigación'
                    },
                    children: [
                        {
                            path: 'personas/:personId/articulos',
                            name: 'persons.articles',
                            component: () => import('@/professors/pages/ArticlesPage.vue'),
                            meta: {
                                description: "Artículos publicados en revistas arbitradas, es decir, trabajos de investigación evaluados por pares académicos, como artículos científicos, artículos de investigación o artículos de revisión publicados en revistas nacionales o internacionales."
                            }
                        },
                        {
                            path: 'personas/:personId/trabajos',
                            name: 'persons.creations',
                            component: () => import('@/professors/pages/CreationsPage.vue'),
                            meta: {
                                description: "Registro y validación de obras académicas, entendidas como productos de investigación o creación intelectual, como libros, capítulos de libro u obras publicadas por editoriales académicas, permitiendo aceptar o rechazar la autoría de los registros precargados desde el SIIA."
                            }
                        },
                        {
                            path: 'personas/:personId/patentes',
                            name: 'persons.patents',
                            component: () => import('@/professors/pages/PatentsPage.vue'),
                            meta: {
                                description: "Registro y validación de obras académicas, entendidas como creaciones intelectuales originales con fines científicos, didácticos o de divulgación"
                            }
                        },
                        {
                            path: 'personas/:personId/productos',
                            name: 'persons.products',
                            component: () => import('@/professors/pages/ResearchProductsPage.vue'),
                            meta: {
                                description: "Registro y gestión de productos académicos, derivados de actividades de investigación, docencia y divulgación científica, tales como libros, capítulos de libro, informes académicos, participaciones en publicaciones y otros productos, permitiendo su actualización y control por el personal académico autorizado."
                            }
                        },
                    ]
                },

                // 🚀 INVESTIGACIÓN - DESARROLLOS TÉCNICOS
                {
                    path: '/',
                    component: () => import('@/professors/modules/TechnologyDevelopmentModule.vue'),
                    meta: {
                        title: 'Investigación > Desarrollo tecnológico'
                    },
                    children: [{
                        path: 'personas/:personId/desarrollos-tecnologicos',
                        name: 'persons.technology-developments',
                        component: () => import('@/professors/pages/TechnologyDevelopmentsPage.vue'),
                        meta: {
                            description: "Registro y gestión de desarrollos de tecnologías y técnicas, entendidas como metodologías, procesos, procedimientos analíticos o experimentales desarrollados o implementados por el personal académico en actividades de investigación, docencia o vinculación, indicando el rol de participación, número de participantes y periodo de desarrollo."
                        }
                    }]
                },

                // 🚀 INVESTIGACIÓN - ESTANCIAS DE INVESTIGACIÓN
                {
                    path: '/',
                    component: () => import('@/professors/modules/TrainingsModule.vue'),
                    meta: {
                        title: 'Investigación > Estancias de investigación'
                    },
                    children: [
                        {
                            path: 'personas/:personId/formaciones',
                            name: 'persons.trainings',
                            component: () => import('@/professors/pages/TrainingsPage.vue'),
                            meta: {
                                description: "Registro de estancias de investigación y actividades formativas, detallando el proyecto asociado, el alumno participante, su nivel académico y el periodo de realización, permitiendo el seguimiento de la participación del personal académico en la formación de estudiantes."
                            }
                        }
                    ]
                },

                // 🚀 INVESTIGACIÓN - ARBITRAJES DE INVESTIGACIÓN
                {
                    path: '/',
                    component: () => import('@/professors/modules/ResearchArbitrationsModule.vue'),
                    meta: {
                        title: 'Investigación > Arbitrajes de investigación'
                    },
                    children: [
                        {
                            path: 'personas/:personId/arbitrajes/investigacion',
                            name: 'persons.research-arbitrations',
                            component: () => import('@/professors/pages/ResearchArbitrationsPage.vue'),
                            meta: {
                                description: "Registro y gestión de arbitrajes de investigación académica (tipo, trabajo, evento y fecha)."
                            }
                        }
                    ]
                },

                // 🚀 DIVULGACION - CONFERENCIAS
                {
                    path: '/',
                    component: () => import('@/professors/modules/ConferenceModule.vue'),
                    meta: {
                        title: 'Divulgación > Conferencias'
                    },

                    children: [
                        {
                            path: 'personas/:personId/conferencias',
                            name: 'persons.conferences',
                            component: () => import('@/professors/pages/ConferencesPage.vue'),
                            meta: {
                                description: "Registro de conferencias académicas en las que el académico ha participado, indicando título, institución o lugar del evento, país, número de autores y fecha. Permite crear, actualizar y eliminar registros de conferencias."
                            }
                        },
                    ]
                },

                // 🚀 DIVULGACIÓN - PRESENTACIONES MULTIMEDIA
                {
                    path: '/',
                    component: () => import('@/professors/modules/PresentationsModule.vue'),
                    meta: {
                        title: 'Divulgación > Presentaciones multimedia'
                    },
                    children: [{
                        path: 'personas/:personId/presentaciones',
                        name: 'persons.presentations',
                        component: () => import('@/professors/pages/PresentationsPage.vue'),
                        meta: {
                            description: "Una presentación multimedia es cualquier exposición o demostración de información que utiliza distintos medios digitales o visuales para comunicar un mensaje. Generalmente combina texto, imágenes, audio, video, animaciones o gráficos para transmitir información de manera clara y atractiva."
                        }
                    }]
                },

                // 🚀 DIVULGACION - REUNIONES ACADÉMICAS COLECTIVAS
                {
                    path: '/',
                    component: () => import('@/professors/modules/AcademicEventsModule.vue'),
                    meta: {
                        title: 'Divulgación > Reuniones académicas colectivas'
                    },
                    children: [{
                        path: 'personas/:personId/eventos/trabajos',
                        name: 'persons.events.works',
                        component: () => import('@/professors/pages/WorkEventsPage.vue'),
                        meta: {
                            description: "Encuentros organizados que reúnen a varios miembros de la comunidad académica (profesores, investigadores o estudiantes) para presentar, discutir o compartir trabajos, proyectos, resultados de investigación o experiencias educativas. El enfoque está en la participación colectiva y el intercambio de conocimiento, más que en la exposición individual."
                        }
                    }]
                },

                // 🚀 DIVULGACIÓN - FERIAS DE LA CIENCIA
                {
                    path: '/',
                    component: () => import('@/professors/modules/FairsModule.vue'),
                    meta: {
                        title: 'Divulgación > Ferias de la ciencia',
                    },
                    children: [{
                        path: 'personas/:personId/ferias',
                        name: 'persons.fairs',
                        component: () => import('@/professors/pages/FairsPage.vue'),
                        meta: {
                            description: "Son eventos públicos o académicos donde se presentan trabajos, experimentos, proyectos o investigaciones científicas, generalmente realizados por estudiantes, investigadores o equipos académicos. Su objetivo es divulgar conocimiento científico, fomentar la curiosidad, y promover la participación y la interacción entre los participantes y el público asistente."
                        }
                    }]
                },

                // 🚀 DIVULGACION - ORGANIZACIÓN DE ACTIVIDADES
                {
                    path: '/',
                    component: () => import('@/professors/modules/AcademicActivitiesModule.vue'),
                    meta: {
                        title: 'Divulgación > Organización de actividades'
                    },
                    children: [{
                        path: 'personas/:personId/actividades/academicas',
                        name: 'persons.academic-activities',
                        component: () => import('@/professors/pages/AcademicActivitiesPage.vue'),
                        meta: {
                            description: "Se refiere a la planificación, coordinación y ejecución de eventos académicos o culturales organizados por una institución educativa, departamento o grupo académico, con el objetivo de fomentar la participación, el aprendizaje y la difusión de conocimiento."
                        }
                    }],

                },

                // 🚀 DIVULGACIÓN - PROGRAMAS DE RADIO Y TELEVISIÓN
                {
                    path: '/',
                    component: () => import('@/professors/modules/BroadcastsModule.vue'),
                    meta: {
                        title: 'Divulgación > Programas de radio y televisión'
                    },
                    children: [{
                        path: 'personas/:personId/transmisiones',
                        name: 'persons.broadcasts',
                        component: () => import('@/professors/pages/BroadcastsPage.vue'),
                        meta: {
                            description: "Se refiere a la participación en la difusión de conocimiento académico o científico a través de medios masivos como radio, televisión o plataformas de streaming institucionales. El objetivo es informar, educar o sensibilizar a la sociedad sobre temas de interés académico, científico, cultural o educativo."
                        }
                    }]
                },

                // 🚀 DIVULGACIÓN - PUBLICACIONES DE DIVULGACIÓN
                {
                    path: '/',
                    component: () => import('@/professors/modules/PublicationsModule.vue'),
                    meta: {
                        title: 'Divulgación > Publicaciones de divulgación'
                    },
                    children: [{
                        path: 'personas/:personId/publicaciones',
                        name: 'persons.publications',
                        component: () => import('@/professors/pages/PublicationsPage.vue'),
                        meta: {
                            description: "Se refiere a la producción de material escrito dirigido al público general o especializado no académico con el fin de divulgar conocimiento científico, tecnológico, cultural o educativo. Estas publicaciones no necesariamente están arbitradas por revistas académicas formales, pero cumplen la función de difundir ideas, hallazgos o información de manera accesible."
                        }
                    }]
                },

                // 🚀 APOYO A SERVICIOS - SERVICIOS
                {
                    path: '/',
                    component: () => import('@/professors/modules/ServicesModule.vue'),
                    meta: {
                        title: 'Apoyo a servicios'
                    },
                    children: [{
                        path: 'personas/:personId/servicios',
                        name: 'persons.services',
                        component: () => import('@/professors/pages/ServicesPage.vue'),
                        meta: {
                            description: "Participación de una persona (profesor, investigador o personal) en actividades de soporte o asistencia que contribuyen al funcionamiento de proyectos, servicios, programas o equipos dentro de una institución, ya sea académica, de investigación o de extensión."
                        }
                    }]
                },

                // 🚀 OTRAS ACTIVIDADES - OTRAS ACTIVIDADES
                {
                    path: '/',
                    component: () => import('@/professors/modules/OtherActivitiesModule.vue'),
                    meta: {
                        title: 'Otras actividades'
                    },
                    children: [{
                        path: 'personas/:personId/actividades/otras',
                        name: 'persons.activities.other',
                        component: () => import('@/professors/pages/OtherActivitiesPage.vue'),
                        meta: { 
                            description: "actividades son aquellas que realiza un académico para complementar su labor docente, investigativa o institucional, sin encajar en categorías formales como publicaciones, divulgación o apoyo a servicios. Reflejan su participación activa, compromiso institucional y contribución al desarrollo académico y profesional."
                        }
                    }]
                },

                // IMPORTAR CURSOS
                {
                    path: '/cursos/importar',
                    name: 'courses.import',
                    component: () => import('@/courses/pages/ImportCoursePage.vue'),
                    meta: {
                        role: Role.ADMINISTRATOR_NAME,
                        title: 'Importar asignaturas impartidas en los posgrados de la UNAM'
                    }
                },

                // IMPORTAR JURADOS
                {
                    path: '/jurados/importar',
                    name: 'juries.import',
                    component: () => import('@/juries/pages/ImportJuriesPage.vue'),
                    meta: {
                        role: Role.ADMINISTRATOR_NAME,
                        title: 'Importar participaciones como jurado de examenes profesionales y de grado'
                    }
                },

                // IMPORTAR TUTORIAS
                {
                    path: '/tutorias/importar',
                    name: 'tutorings.import',
                    component: () => import('@/tutorings/pages/ImportTutoringsPage.vue'),
                    meta: {
                        role: Role.ADMINISTRATOR_NAME,
                        title: 'Importar participaciones en comités tutorales de alumnos de posgrado'
                    }
                },

                // IMPORTAR PROYECTOS
                {
                    path: '/proyectos/importar',
                    name: 'projects.import',
                    component: () => import('@/projects/pages/ImportProjectsPage.vue'),
                    meta: {
                        role: Role.ADMINISTRATOR_NAME,
                        title: 'Importar proyectos'
                    }
                },

                // IMPORTAR TITULACIONES DE POSGRADO
                {
                    path: '/titulaciones/importar',
                    name: 'degrees.import',
                    component: () => import('@/degrees/pages/ImportDegreesPage.vue'),
                    meta: {
                        role: Role.ADMINISTRATOR_NAME,
                        title: 'Importar exámenes de grado de posgrado'
                    }
                },

                // IMPORTAR PROGRAMAS
                {
                    path: '/programas/importar',
                    name: 'programs.import',
                    component: () => import('@/programs/pages/ImportProgramsPage.vue'),
                    meta: {
                        role: Role.ADMINISTRATOR_NAME,
                        title: 'Importar programas PAIP y PAL'
                    }
                },
                
                // IMPORTAR RECONOCIMIENTOS
                {
                    path: '/reconocimientos/importar',
                    name: 'recognitions.import',
                    component: () => import('@/recognitions/pages/ImportRecognitionsPage.vue'),
                    meta: {
                        role: Role.ADMINISTRATOR_NAME,
                        title: 'Importar reconocimientos DGAPA'
                    }
                },

                // LISTA DE PROFESORES
                {
                    path: '/profesores',
                    name: 'professors.list',
                    component: () => import('@/professors/pages/ProfessorsListPage.vue'),
                    meta: {
                        role: [Role.ADMINISTRATOR_NAME, Role.POSTGRADE_NAME, Role.COUNCIL_NAME],
                        title: 'Profesores'
                    }
                },

                // REPORTE
                {
                    path: '/',
                    component: () => import('@/professors/modules/ReportModule.vue'),
                    meta: {
                        title: 'Reporte'
                    },
                    children: [
                        {
                            path: 'personas/:personId/reporte',
                            name: 'persons.reports',
                            component: () => import('@/professors/pages/ReportsPage.vue'),
                        }
                    ]
                },

                // COMPARATIVO
                {
                    path: '/',
                    component: () => import('@/professors/modules/ComparativeModule.vue'),
                    meta: {
                        title: 'Comparativo'
                    },
                    children: [
                        {
                            path: 'personas/:personId/comparativo',
                            name: 'persons.comparative',
                            component: () => import('@/professors/pages/ComparativePage.vue'),
                        }
                    ]
                },
            ]
        },

        // RUTAS QUE NO REQUIEREN AUTENTICACIÓN
        {
            path: '/',
            component: () => import('@/auth/layouts/AuthLayout.vue'),
            children: [{
                path: '/login',
                name: 'login',
                component: () => import('@/auth/pages/LoginPage.vue'),
            }, {
                path: '/reestablecer-password',
                name: 'forgot-password',
                component: () => import('@/auth/pages/ForgotPasswordPage.vue'),
            }, {
                path: '/reestablecer-password/:token',
                name: 'reset-password',
                component: () => import('@/auth/pages/ResetPasswordPage.vue'),
            }]
        }, {
            path: '/:catchAll(.*)',
            name: 'not-found',
            component: () => import('@/shared/components/PageNotFound.vue'),
        }]
});

router.beforeEach(verifyTokenMiddleware);

router.beforeEach(verifyRoleMiddleware);

router.beforeEach(verifyPoliciesMiddleware);

router.beforeEach(storeClassificationsMiddleware);

router.beforeEach(verifyTermsMiddleware);

export { router };